<template>
  <div style="display:flex; flex-direction:column; width:100%; height:100%; min-height:0;">
    <!-- Estado: Sin viewer activo - Mostrar selector -->
    <div v-if="!viewerActive"
         class="d-flex flex-column align-center justify-center pa-6"
         style="width:100%; height:100%;">

      <!-- Cargando Token -->
      <template v-if="$store.forgeStore.state.loadingToken">
        <v-progress-circular :size="50" :width="2" color="#2D4B95" indeterminate />
        <div class="mt-4">Cargando Autodesk Forge</div>
      </template>

      <!-- Selector de modelo -->
      <template v-else>
        <v-icon size="64" color="grey lighten-1" class="mb-4">mdi-file-cad-box</v-icon>
        <h3 class="mb-2 grey--text text--darken-1">Selecciona un modelo 3D</h3>
        <p class="mb-6 grey--text text-center">Elige un archivo para visualizar en el visor 3D</p>

        <v-autocomplete
          v-model="internalSelectedFile"
          :loading="$store.forgeStore.state.loadingFiles"
          :items="$store.forgeStore.state.forgeFiles"
          :item-disabled="isFileDisabled"
          item-text="name"
          item-value="id"
          return-object
          outlined
          dense
          label="Seleccione un archivo"
          hide-details
          style="max-width: 500px; width: 100%;"
          class="mb-4"
        >
          <template v-slot:item="{ item, attrs, on }">
            <v-list-item v-bind="attrs" v-on="on">
              <v-list-item-avatar
                size="36"
                :color="isFileDisabled(item) ? 'grey lighten-3' : 'blue lighten-5'"
                rounded
              >
                <v-icon
                  :color="isFileDisabled(item) ? 'grey lighten-1' : 'blue darken-1'"
                  size="20"
                >
                  {{ isFileProcessing(item) ? 'mdi-progress-clock' : 'mdi-file-cad' }}
                </v-icon>
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title
                  :class="isFileDisabled(item) ? 'grey--text text--lighten-1' : ''"
                  class="font-weight-medium"
                >
                  {{ item.name }}
                </v-list-item-title>

                <v-list-item-subtitle v-if="isFileProcessing(item)" class="d-flex align-center mt-1">
                  <v-chip x-small color="orange lighten-4" text-color="orange darken-3" class="mr-1">
                    <v-icon x-small left>mdi-clock-outline</v-icon>
                    Procesando
                  </v-chip>
                  <span class="caption grey--text">Intenta en unos minutos</span>
                </v-list-item-subtitle>

                <v-list-item-subtitle v-else-if="!isFileDisabled(item)" class="d-flex align-center mt-1">
                  <v-chip x-small color="green lighten-4" text-color="green darken-3">
                    <v-icon x-small left>mdi-check-circle</v-icon>
                    Listo para cargar
                  </v-chip>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </template>

          <template v-slot:no-data>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title class="grey--text text-center">
                  <v-icon class="mr-1">mdi-file-search-outline</v-icon>
                  No se encontraron archivos disponibles
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-autocomplete>

        <v-btn
          color="#2D4B95"
          dark
          rounded
          :disabled="!internalSelectedFile"
          @click="loadSelectedModel"
        >
          <v-icon left>mdi-cube-scan</v-icon>
          Cargar modelo 3D
        </v-btn>
      </template>
    </div>

    <!-- Estado: Viewer activo -->
    <div v-else style="width:100%; height:100%; position:relative; display:flex; flex-direction:column;">
      <!-- Barra superior con información del archivo cargado -->
      <div class="viewer-toolbar d-flex align-center justify-space-between pa-2" style="background:#f5f5f5; border-bottom:1px solid #e0e0e0; flex-shrink:0;">
        <div class="d-flex align-center">
          <v-icon small color="#2D4B95" class="mr-2">mdi-file-cad</v-icon>
          <span class="caption font-weight-medium">{{ currentLoadedFile ? currentLoadedFile.name : 'Modelo 3D' }}</span>
        </div>
        <v-btn
          x-small
          text
          color="#2D4B95"
          @click="changeModel"
        >
          <v-icon x-small left>mdi-swap-horizontal</v-icon>
          Cambiar modelo
        </v-btn>
      </div>

      <PlanimetriaForgeViewer
        :forgeSelectedFile="currentLoadedFile"
        :fetchToken="false"
        style="flex:1; min-height:0;"
      />
    </div>
  </div>
</template>

<script>
import PlanimetriaForgeViewer from '@/components/Views/Planimetria/ForgeViewer.vue'

export default {
	name: 'ForgeViewer',
	components: { PlanimetriaForgeViewer },
	props: {
		selectedFile: {
			type: Object,
			default: null
		}
	},
	data: () => ({
		internalSelectedFile: null,
		viewerActive: false,
		currentLoadedFile: null,
	}),
	computed: {
		selectedProject() {
			return this.$store.projectsStore.state.selectedProject
		},
		projectId() {
			return this.$store.projectsStore.state.selectedProject?.id
		},
		storageKey() {
			return `forgeViewer_selectedFile_project_${this.projectId}`
		},
		projectMainFile() {
			return this.$store.forgeStore.state.projectMainFile
		},
		mainModelFileId() {
			return this.projectMainFile?.file_id
		},
	},
	watch: {
		selectedProject(newProject, oldProject) {
			if (newProject?.id !== oldProject?.id) {
				this.viewerActive = false
				this.currentLoadedFile = null
				this.internalSelectedFile = null
				if (newProject?.id) {
					this.$store.forgeStore.dispatch('fetchForgeFiles', { project_id: newProject.id })
						.then(() => { this.loadDefaultFile() })
				}
			}
		}
	},
	mounted() {
		this.$store.forgeStore.dispatch('fetchForgeToken', ['data:read', 'bucket:read'])
		if (this.projectId) {
			this.$store.forgeStore.dispatch('fetchForgeFiles', { project_id: this.projectId })
				.then(() => { this.loadDefaultFile() })
		}
	},
	methods: {
		getSavedFileId() {
			try {
				const saved = localStorage.getItem(this.storageKey)
				return saved ? JSON.parse(saved) : null
			} catch(e) {
				return null
			}
		},

		saveFileId(fileId) {
			try {
				localStorage.setItem(this.storageKey, JSON.stringify(fileId))
			} catch(e) {
				console.warn('Could not save file selection:', e)
			}
		},

		loadDefaultFile() {
			const forgeFiles = this.$store.forgeStore.state.forgeFiles || []

			// Prioridad 1: forge_main_model del proyecto
			if (this.mainModelFileId) {
				const mainFile = forgeFiles.find(f => f.id === this.mainModelFileId)
				if (mainFile && !this.isFileDisabled(mainFile)) {
					this.internalSelectedFile = mainFile
					this.loadSelectedModel()
					return
				}
			}

			// Prioridad 2: archivo guardado en localStorage para este proyecto
			const savedFileId = this.getSavedFileId()
			if (savedFileId) {
				const savedFile = forgeFiles.find(f => f.id === savedFileId)
				if (savedFile && !this.isFileDisabled(savedFile)) {
					this.internalSelectedFile = savedFile
					this.loadSelectedModel()
					return
				}
				localStorage.removeItem(this.storageKey)
			}
		},

		loadSelectedModel() {
			if (!this.internalSelectedFile) return
			this.$emit('file-selected', this.internalSelectedFile)
			this.saveFileId(this.internalSelectedFile.id)
			this.currentLoadedFile = this.internalSelectedFile
			this.viewerActive = true
		},

		changeModel() {
			this.viewerActive = false
			this.currentLoadedFile = null
		},

		isFileProcessing(file) {
			const forgeFile = file.forge_file
			return !forgeFile || (!forgeFile.urn && !forgeFile.translate)
		},

		isFileDisabled(file) {
			return this.isFileProcessing(file)
		},
	}
}
</script>
